import * as Sentry from "@sentry/react";
import cx from "classnames";
import { Form, Formik } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Checkbox from "../../common/components/Forms/Checkbox/Checkbox";
import SimpleContentModal from "../../common/components/Modals/SimpleContentModal";
import { getProductTypeAsString } from "../../common/shared/enumHelpers";
import { StringDictionary } from "../../common/types/StringDictionary";
import { agreeDisclosure, cleanSkin, getSectionContent, setCurrentPageProgress } from "../api/ApiAppForm";
import AFLayout from "../components/AFLayout";
import AFNavigation from "../components/AFNavigation";
import { IViewContext, ViewContext } from "../providers/ViewStateProvider";
import { PageType } from "../enums/PageType";
import { useAFForm } from "../hooks/useAFForm";
import { IPageInfo } from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import styles from "../scss/AFConsent.module.scss";
import { redirectTo404Page } from "../shared/AppFormHelper";

interface IAFConsentProps {
    applicationId: string;
    consent: string;
}

interface IAFConsentData {
    pageNumber: number;
    param: string;
    label: string;
    isFinal: boolean;
}

const consentList = [
    {
        pageNumber: 1,
        param: "disclosure",
        label: "1/2",
        isFinal: false
    },
    {
        pageNumber: 2,
        param: "cleanskin",
        label: "2/2",
        isFinal: true
    }
];

const AFConsentPage: React.FC = () => {
    const contentHost = useRef();
    const history = useHistory();
    const params = useParams<IAFConsentProps>();
    const applicationId = parseInt(params.applicationId);
    const consentParam = params.consent;
    const { setError } = useContext<IViewContext>(ViewContext);

    const [consent, setConsent] = useState<IAFConsentData>();
    const [consentContent, setConsentContent] = useState("");
    const [currentConsent, setCurrentConsent] = useState(0);
    const [modalHeader, setModalHeader] = useState<string>();
    const [modalContent, setModalContent] = useState<string>();
    const [isOpenContent, setIsOpenContent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isCleanSkin, setIsCleanSkin] = useState(false);
    const [declarationLinkText, setDeclarationLinkText] = useState("");

    const { selectors, actions } = useContext(AppFormControllerContext);
    const {
        getCurrentPage,
        checkValidApplication
    } = useAFForm({ pageType: PageType.Consent, currentClientNumber: 0 });

    const handleNextPage = (): void => {
        const pageInfo: IPageInfo = {
            pageType: PageType.Signature,
            currentClientNumber: 1
        }
        setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
        )
        history.push(`/app-form/submit/${applicationId}/ready`);
    };

    const handleBack = (e): void => {
        e.preventDefault();
        const info = consentList[currentConsent - 1];
        if (info.pageNumber !== 1) {
            history.push(`/app-form/submit/${applicationId}/consent/${consentList[info.pageNumber - 2].param}`);
            return;
        }
        history.push(`/app-form/submit/${applicationId}/payment`);
    };

    const agreeConsent = (): void => {
        if (consent?.isFinal) {
            setIsLoading(true);
            cleanSkin(applicationId, selectors.getQuoteGroupId(), isCleanSkin)
                .then((res) => {
                    if (!res) return;
                    handleNextPage();
                })
                .catch(() => {
                    setError("There was a problem recording your consent.");
                    if (typeof Sentry !== 'undefined') {
                        Sentry.captureMessage(`Error occured for ApplicationId ${applicationId} on the Consent page. There was a problem recording cleanskin.`, "error");
                    }
                    setIsLoading(false);
                });
            return;
        }
        switch (consent?.pageNumber) {
            case 1:
                agreeDisclosure(applicationId, selectors.getQuoteGroupId())
                    .then((res) => {
                        if (!res) return;
                        handleNextConsent();
                    })
                    .catch(() => {
                        setError("There was a problem recording your agreement.");
                        if (typeof Sentry !== 'undefined') {
                            Sentry.captureMessage(`Error occured for ApplicationId ${applicationId} on the Consent page. There was a problem recording disclosure.`, "error");
                        }
                    });
                return;
            default:
        }
    };

    const handleNextConsent = (): void => {
        const info = consentList[currentConsent];
        history.push(`/app-form/submit/${applicationId}/consent/${info?.param}`);
    };

    const handleConsentInfo = (): void => {
        const info = consentList?.find((i) => i.param === consentParam);
        if (!info || !info?.pageNumber) {
            redirectTo404Page();
            return;
        }
        setConsent(info);
        setCurrentConsent(info?.pageNumber);
    };


    const displayContentModal = (headerKey: string, contentKey: string): void => {
        var sectionContent = selectors.getSectionContent();
        if (!sectionContent) return;

        var header = sectionContent[headerKey];
        var content = sectionContent[contentKey];

        if (!header || !content) return;

        setModalHeader(header);
        setModalContent(content);
        setIsOpenContent(true);
    };


    const setContent = (content: StringDictionary): void => {
        setDeclarationLinkText(content.consent_DisclosureLink as string);
        setConsentContent(content.consent as string || "");
    };

    useEffect(() => {
        var existingContent = selectors.getSectionContent();
        if (existingContent) {
            setContent(existingContent as StringDictionary);
            return;
        }
        const applicationId = selectors.getApplicationId();
        const quoteGroupId = selectors.getQuoteGroupId();
        getSectionContent(applicationId, quoteGroupId).then((res) => {
            actions.setSectionContent(res || {});
            setContent(res || {});
        });
    }, []); // Only run once on startup

    useEffect(() => {
        if (!consentParam) return;
        checkValidApplication(String(applicationId), selectors);
        handleConsentInfo();
    }, [consentParam]);

    useEffect(() => {
        abTesting.convertAll(19, "", getProductTypeAsString, 0);
    }, []);

    return (
        <AFLayout currentPage={getCurrentPage()}>
            <SimpleContentModal
                isOpen={isOpenContent}
                onClose={() => {
                    setIsOpenContent(false);
                }}
                header={modalHeader}
                content={modalContent}
            />

            <div className={cx(styles["af-consent"], styles["layout"])}>
                <Formik initialValues={{}} onSubmit={agreeConsent}>
                    {() => (
                        <Form autoComplete="off"
                            className={cx(
                                styles["af-consent-container"],
                                styles["layout-flex-column--centered"]
                            )}>
                            <div className={cx(styles["consent-title-label"])}>
                                Declaration and consent
                            </div>
                            <div className={styles["af-consent-content"]}>
                                {consent?.label && (
                                    <h2 className={cx(styles["af-payment-label"])}>
                                        {consent?.label}
                                    </h2>
                                )}

                                {consent?.pageNumber === 1 && (
                                    <>
                                        <p>
                                            I/We have read and accept{" "}
                                            <a href="#" onClick={() => displayContentModal("insurerDeclarationHeader", "allProducts")}>{declarationLinkText}</a>
                                            {" "}and agree to be bound by its terms and conditions.
                                        </p>
                                        <div ref={contentHost} dangerouslySetInnerHTML={{ __html: consentContent }}></div>
                                        <p>
                                            I/We agree to{" "}
                                            <a href="#" onClick={() => displayContentModal("ldTermsHeader", "ldTermsContent")}>LifeDirect Terms &amp; Conditions.</a>
                                        </p>
                                        <p>
                                            By pressing the button below, you confirm that you agree with all of the above.
                                        </p>
                                    </>
                                )}

                                {consent?.pageNumber === 2 && (
                                    <div className={cx("layout-flex-row--centered")}>
                                        <Checkbox
                                            className="space-l"
                                            checked={isCleanSkin}
                                            name={"cleanskin"}
                                            size="2xl"
                                            isDisabled={isLoading}
                                            onChange={() => setIsCleanSkin(!isCleanSkin)}>
                                        </Checkbox>
                                        <span className={cx(
                                            "gap-l",
                                            "text-align--center",
                                            styles["af-payment-info"]
                                        )}>
                                            If this application is approved by the insurer with no additional terms, would you like LifeDirect to request they issue it as soon as possible?
                                        </span>
                                    </div>
                                )}
                                <AFNavigation
                                    handleBack={handleBack}
                                    skipIsVisible={false}
                                    forwardIsVisible={true}
                                    forwardIsDisabled={false}
                                    forwardType="submit"
                                    forwardText={consent?.pageNumber === 1 ? "I/We Agree" : "Next"}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </AFLayout>
    );
};

export default AFConsentPage;
